<script setup>
/**
 * WaveformAudioPlayer
 *
 * Drop-in visual replacement for the plain `<input type="range">` seek bar
 * in `message/chips/Audio.vue`, using the already-installed `wavesurfer.js`
 * (previously only used for the outgoing voice-note *recorder*, never for
 * *playback* - see AudioRecorder.vue for the existing usage this mirrors).
 *
 * Deliberately does not own playback state: it attaches to the *existing*
 * <audio> element via wavesurfer's `media` option, so all of Audio.vue's
 * already-working play/pause/mute/speed/download/transcript logic keeps
 * driving that element untouched - this component only renders the
 * waveform and lets the user click/drag it to seek, both of which
 * wavesurfer keeps in sync with the attached element automatically.
 *
 * Decoding a remote audio file client-side can fail or be slow on a bad
 * connection; the parent is expected to fall back to the plain range input
 * on the `error` event (progressive enhancement, not a hard dependency).
 *
 * wavesurfer computes its canvas layout as
 * `Math.ceil(totalWidth / Math.min(MAX_CANVAS_WIDTH, container.clientWidth, totalWidth))`
 * (see node_modules/wavesurfer.js/dist/renderer.js, renderMultiCanvas) - if
 * `create()` runs before this container has been given a real layout width
 * (clientWidth still 0, e.g. right after mount inside a popover/flex
 * container that hasn't settled yet), that division by zero cascades into
 * an invalid typed-array length and wavesurfer throws instead of emitting
 * its own 'error' event. waitForNonZeroWidth() closes that race.
 */
import { onBeforeUnmount, onMounted, useTemplateRef } from 'vue';

const props = defineProps({
  mediaElement: { type: Object, default: null },
  height: { type: Number, default: 28 },
});

const emit = defineEmits(['ready', 'error']);

const containerRef = useTemplateRef('containerRef');
let wavesurfer = null;

const readCssColor = (variable, fallback) => {
  if (typeof window === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(variable)
    .trim();
  return value ? `rgb(${value})` : fallback;
};

const WIDTH_WAIT_TIMEOUT_MS = 2000;

/** Resolves once `el` has a non-zero clientWidth, or after a timeout so a
 * permanently hidden/zero-width container can't hang initialization forever. */
const waitForNonZeroWidth = el =>
  new Promise(resolve => {
    if (el.clientWidth > 0) {
      resolve();
      return;
    }
    const observer = new ResizeObserver(() => {
      if (el.clientWidth > 0) {
        observer.disconnect();
        resolve();
      }
    });
    observer.observe(el);
    setTimeout(() => {
      observer.disconnect();
      resolve();
    }, WIDTH_WAIT_TIMEOUT_MS);
  });

onMounted(async () => {
  if (!props.mediaElement || !containerRef.value) {
    emit('error');
    return;
  }

  try {
    await waitForNonZeroWidth(containerRef.value);
    // Component may have been unmounted while waiting for layout/width.
    if (!containerRef.value) return;

    const { default: WaveSurfer } = await import('wavesurfer.js');
    // Component may have been unmounted while the chunk was loading.
    if (!containerRef.value) return;

    if (containerRef.value.clientWidth === 0) {
      // Still zero after the timeout - creating wavesurfer now would hit
      // the same division-by-zero, so fail cleanly into the fallback
      // instead of letting it throw.
      emit('error');
      return;
    }

    wavesurfer = WaveSurfer.create({
      container: containerRef.value,
      media: props.mediaElement,
      height: props.height,
      waveColor: readCssColor('--slate-7', '#CBD5E1'),
      progressColor: readCssColor('--slate-12', '#1E293B'),
      cursorWidth: 0,
      barWidth: 2,
      barGap: 2,
      barRadius: 2,
      normalize: true,
      interact: true,
    });

    wavesurfer.once('ready', () => emit('ready'));
    wavesurfer.once('error', () => emit('error'));
  } catch (error) {
    emit('error');
  }
});

onBeforeUnmount(() => {
  wavesurfer?.destroy();
  wavesurfer = null;
});
</script>

<template>
  <div ref="containerRef" class="w-full cursor-pointer" />
</template>
